import store from '@/store'
import i18n from '@/libs/i18n'

export const EMPTY_STATE_STYLE_ID = 'novti-pb-form-placeholder-styles'
export const PICKER_MENU_Z_INDEX = 10050

/**
 * Whether the page/component has no usable form id.
 *
 * @param {*} formId
 * @returns {boolean}
 */
export function isMissingFormId(formId) {
  return formId == null || formId === '' || formId === 'undefined' || formId === 'null'
}

/**
 * The page document's formId is the source of truth when opening the builder.
 * The GrapesJS component `form` attribute can be stale leftover HTML.
 *
 * @param {{ data?: { formId?: string } } | null | undefined} currentPage
 * @returns {string|null}
 */
export function resolveCanvasFormId(currentPage) {
  const formId = currentPage?.data?.formId
  return isMissingFormId(formId) ? null : formId
}

/**
 * Initial Component Settings picker value. Prefer the page form so a stale
 * canvas attribute cannot show the wrong selected form on load.
 *
 * @param {{ getAttributes?: () => { form?: string } }} component
 * @param {{ data?: { formId?: string } } | null | undefined} [currentPage]
 * @returns {string}
 */
export function resolveInitialTraitFormId(component, currentPage = store.getters['pages/getCurrent']) {
  const pageFormId = resolveCanvasFormId(currentPage)
  if (pageFormId) {
    return pageFormId
  }

  const attrFormId = component?.getAttributes?.()?.form
  return isMissingFormId(attrFormId) ? '' : attrFormId
}

const EDITOR_EVENTS_TO_STOP = [
  'mousedown',
  'mouseup',
  'pointerdown',
  'pointerup',
  'touchstart',
  'click',
  'dblclick',
  'keydown',
  'keyup',
  'keypress',
  'input',
  'focus',
  'blur',
  'dragstart',
]

const EMPTY_STATE_STYLES = `
:host {
  all: initial;
  display: block;
  width: auto;
  max-width: 100%;
  min-width: 0;
  position: relative;
  z-index: 5;
  overflow: hidden;
  box-sizing: border-box;
}

:host(.novti-pb-form-placeholder-host) {
  width: 100%;
}

:host(.is-open) {
  z-index: 10050;
}

.novti-pb-form-placeholder,
.novti-pb-form-placeholder * {
  box-sizing: border-box;
}

.novti-pb-form-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  overflow: visible;
  width: 100%;
  margin: 0;
  padding: 2rem 1.25rem;
  text-align: center;
  background-color: #f8f9fa;
  border: 2px dashed #d8d6de;
  border-radius: 0.5rem;
  font-family: Montserrat, Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.45;
  color: #6e6b7b;
}

.novti-pb-form-placeholder__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin: 0 0 1rem;
  color: #2126cb;
  background-color: rgba(33, 38, 203, 0.08);
  border-radius: 50%;
}

.novti-pb-form-placeholder__icon svg {
  display: block;
  width: 22px;
  height: 22px;
  max-width: 22px;
  max-height: 22px;
}

.novti-pb-form-placeholder__title {
  margin: 0 0 0.35rem;
  padding: 0;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  color: #5e5873;
}

.novti-pb-form-placeholder__description {
  margin: 0 0 1.25rem;
  padding: 0;
  max-width: 20rem;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.5;
  color: #6c757d;
}

.novti-pb-form-picker {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 22rem;
  min-width: 0;
  overflow: visible;
  text-align: left;
  box-sizing: border-box;
}

.novti-pb-form-picker.is-open {
  z-index: 10050;
}

.novti-pb-form-picker--sidebar {
  max-width: 100%;
  overflow: hidden;
}

.novti-pb-form-picker--sidebar .novti-pb-form-picker__menu {
  max-width: 100%;
  box-sizing: border-box;
}

.novti-pb-form-picker__toggle {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 38px;
  padding: 0 0.75rem;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  box-sizing: border-box;
}

.novti-pb-form-picker.is-open .novti-pb-form-picker__toggle,
.novti-pb-form-picker__toggle:focus-within {
  border-color: #2126cb;
  box-shadow: 0 0 0 0.2rem rgba(33, 38, 203, 0.16);
}

.novti-pb-form-picker.is-loading .novti-pb-form-picker__toggle {
  opacity: 0.7;
}

.novti-pb-form-picker__search {
  display: block;
  flex: 1 1 0%;
  width: 0;
  min-width: 0;
  max-width: 100%;
  height: 36px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
  color: #6e6b7b;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 36px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.novti-pb-form-picker__search::placeholder {
  color: #b9b9c3;
}

.novti-pb-form-picker__chevron {
  display: block;
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  margin-left: 0.5rem;
  border-right: 1.5px solid #6e6b7b;
  border-bottom: 1.5px solid #6e6b7b;
  transform: translateY(-1px) rotate(45deg);
}

.novti-pb-form-picker.is-open .novti-pb-form-picker__chevron {
  transform: translateY(2px) rotate(225deg);
}

.novti-pb-form-picker__menu {
  position: fixed;
  top: auto;
  right: auto;
  left: auto;
  z-index: 10050;
  max-height: 16rem;
  margin: 0;
  padding: 0.35rem 0;
  overflow-x: hidden;
  overflow-y: auto;
  list-style: none;
  background-color: #fff;
  border: 0;
  border-radius: 0.375rem;
  box-sizing: border-box;
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);
}

.novti-pb-form-picker__option,
.novti-pb-form-picker__empty {
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  overflow: hidden;
  color: #6e6b7b;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.novti-pb-form-picker__option.is-highlighted,
.novti-pb-form-picker__option:hover {
  color: #2126cb;
  background-color: rgba(33, 38, 203, 0.12);
}

.novti-pb-form-picker__empty {
  color: #b9b9c3;
  cursor: default;
}
`

/**
 * Prevent GrapesJS from stealing the first click / keypress on interactive canvas UI.
 *
 * @param {HTMLElement} el
 */
export function isolateFromEditor(el) {
  EDITOR_EVENTS_TO_STOP.forEach(type => {
    el.addEventListener(type, event => {
      event.stopPropagation()
    })
  })
}

/**
 * Inject empty-state styles into a document or shadow root once.
 *
 * @param {Document|ShadowRoot} root
 */
export function injectEmptyStateStyles(root) {
  if (!root) {
    return
  }

  const parent = root.nodeType === 9 ? (root.head || root.documentElement) : root

  if (parent.querySelector(`#${EMPTY_STATE_STYLE_ID}`)) {
    return
  }

  const doc = root.nodeType === 9 ? root : (root.ownerDocument || document)
  const style = doc.createElement('style')
  style.id = EMPTY_STATE_STYLE_ID
  style.textContent = EMPTY_STATE_STYLES
  parent.appendChild(style)
}

function formIconSvg() {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  `
}

function sortedForms() {
  return [...(store.getters['forms/getAll'] || [])]
    .sort((a, b) => (a.description || '').localeCompare(b.description || ''))
}

function loadPickerForms(picker, selectedId = '') {
  picker.setLoading(true)

  return store.dispatch('forms/fetchAll')
    .then(() => {
      picker.setLoading(false)
      picker.setForms(sortedForms())
      picker.setSelected(selectedId)
    })
    .catch(error => {
      console.debug(error)
      picker.setLoading(false)
      picker.setForms([])

      store.dispatch('notify', {
        body: i18n.t('Something went wrong retrieving the available forms'),
        variant: 'danger',
      })
    })
}

/**
 * @param {object} options
 * @param {Document} options.doc
 * @param {(formId: string) => void} options.onSelect
 * @param {string} [options.selectedId]
 * @param {'canvas'|'sidebar'} [options.variant]
 * @param {(open: boolean) => void} [options.onOpenChange]
 */
export function createFormPicker({
  doc,
  onSelect,
  selectedId = '',
  variant = 'canvas',
  onOpenChange,
}) {
  const picker = doc.createElement('div')
  picker.className = 'novti-pb-form-picker'

  if (variant === 'sidebar') {
    picker.classList.add('novti-pb-form-picker--sidebar')
  }

  const toggle = doc.createElement('div')
  toggle.className = 'novti-pb-form-picker__toggle'

  const input = doc.createElement('input')
  input.type = 'text'
  input.className = 'novti-pb-form-picker__search'
  input.setAttribute('size', '1')
  input.setAttribute('autocomplete', 'off')
  input.setAttribute('autocorrect', 'off')
  input.setAttribute('spellcheck', 'false')
  input.setAttribute('role', 'combobox')
  input.setAttribute('aria-autocomplete', 'list')
  input.setAttribute('aria-expanded', 'false')
  input.setAttribute('aria-haspopup', 'listbox')
  input.placeholder = i18n.t('Select a form')

  toggle.appendChild(input)

  const chevron = doc.createElement('span')
  chevron.className = 'novti-pb-form-picker__chevron'
  chevron.setAttribute('aria-hidden', 'true')
  toggle.appendChild(chevron)

  const menu = doc.createElement('ul')
  menu.className = 'novti-pb-form-picker__menu'
  menu.hidden = true
  menu.setAttribute('role', 'listbox')

  picker.appendChild(toggle)
  picker.appendChild(menu)

  isolateFromEditor(picker)
  menu.addEventListener('wheel', event => event.stopPropagation())

  let forms = []
  let filtered = []
  let highlightedIndex = -1
  let isOpen = false
  let currentSelectedId = selectedId || ''
  const win = doc.defaultView

  const syncInputFromSelection = () => {
    const selected = forms.find(form => form.id === currentSelectedId)
    input.value = selected ? selected.description : ''
    input.title = selected ? selected.description : ''
  }

  const positionMenu = () => {
    const rect = toggle.getBoundingClientRect()
    const viewportHeight = win?.innerHeight || 0
    const viewportWidth = win?.innerWidth || 0
    const spaceBelow = viewportHeight - rect.bottom - 8
    const maxHeight = 256
    const openUpward = spaceBelow < 160 && rect.top > spaceBelow
    const bounds = getMenuHorizontalBounds(rect)

    menu.style.position = 'fixed'
    menu.style.left = `${Math.round(bounds.left)}px`
    menu.style.width = `${Math.round(bounds.width)}px`
    menu.style.maxWidth = `${Math.round(bounds.width)}px`
    menu.style.right = 'auto'
    menu.style.zIndex = String(PICKER_MENU_Z_INDEX)

    if (openUpward) {
      menu.style.top = 'auto'
      menu.style.bottom = `${Math.round(viewportHeight - rect.top + 6)}px`
      menu.style.maxHeight = `${Math.min(maxHeight, Math.max(rect.top - 16, 120))}px`
    } else {
      menu.style.bottom = 'auto'
      menu.style.top = `${Math.round(rect.bottom + 6)}px`
      menu.style.maxHeight = `${Math.min(maxHeight, Math.max(spaceBelow - 8, 120))}px`
    }

    if (viewportWidth && bounds.left + bounds.width > viewportWidth - 8) {
      menu.style.width = `${Math.max(viewportWidth - 8 - bounds.left, 120)}px`
      menu.style.maxWidth = menu.style.width
    }
  }

  const getMenuHorizontalBounds = rect => {
    if (variant !== 'sidebar') {
      return { left: rect.left, width: Math.max(rect.width, 0) }
    }

    const root = picker.getRootNode()
    const host = root instanceof ShadowRoot ? root.host : picker
    const panel = host?.closest?.('.traits-container') || host?.closest?.('.panel__right')
    const panelRect = panel?.getBoundingClientRect()

    if (!panelRect) {
      return { left: rect.left, width: Math.max(rect.width, 0) }
    }

    const pad = 16

    return {
      left: panelRect.left + pad,
      width: Math.max(panelRect.width - pad * 2, 120),
    }
  }

  const setOpen = nextOpen => {
    isOpen = nextOpen
    picker.classList.toggle('is-open', nextOpen)
    input.setAttribute('aria-expanded', nextOpen ? 'true' : 'false')
    onOpenChange?.(nextOpen)

    if (nextOpen) {
      highlightedIndex = filtered.length ? 0 : -1
      renderMenu()
      positionMenu()
      menu.hidden = false
    } else {
      menu.hidden = true
      syncInputFromSelection()
    }
  }

  const updateHighlight = () => {
    [...menu.children].forEach((option, index) => {
      option.classList.toggle('is-highlighted', index === highlightedIndex)
    })
  }

  const renderMenu = () => {
    menu.innerHTML = ''

    if (!filtered.length) {
      const empty = doc.createElement('li')
      empty.className = 'novti-pb-form-picker__empty'
      empty.textContent = forms.length
        ? i18n.t('No forms found')
        : i18n.t('No forms available')
      menu.appendChild(empty)
      return
    }

    filtered.forEach((form, index) => {
      const option = doc.createElement('li')
      option.className = 'novti-pb-form-picker__option'
      option.setAttribute('role', 'option')
      option.dataset.formId = form.id
      option.textContent = form.description
      option.title = form.description

      if (index === highlightedIndex) {
        option.classList.add('is-highlighted')
      }

      option.addEventListener('mouseenter', () => {
        highlightedIndex = index
        updateHighlight()
      })

      option.addEventListener('mousedown', event => {
        event.preventDefault()
        event.stopPropagation()
        selectForm(form)
      })

      menu.appendChild(option)
    })
  }

  const selectForm = form => {
    if (!form?.id) {
      return
    }

    currentSelectedId = form.id
    input.value = form.description
    setOpen(false)
    onSelect(form.id)
  }

  const applyFilter = () => {
    const query = input.value.trim().toLowerCase()
    filtered = query
      ? forms.filter(form => (form.description || '').toLowerCase().includes(query))
      : [...forms]
    highlightedIndex = filtered.length ? 0 : -1
    renderMenu()
    positionMenu()
  }

  input.addEventListener('focus', () => setOpen(true))
  input.addEventListener('click', () => setOpen(true))
  input.addEventListener('input', () => {
    setOpen(true)
    applyFilter()
  })

  input.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      setOpen(false)
      input.blur()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!isOpen) {
        setOpen(true)
        return
      }

      highlightedIndex = Math.min(highlightedIndex + 1, filtered.length - 1)
      updateHighlight()
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!isOpen) {
        setOpen(true)
        return
      }

      highlightedIndex = Math.max(highlightedIndex - 1, 0)
      updateHighlight()
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      if (!isOpen) {
        setOpen(true)
        return
      }

      selectForm(filtered[highlightedIndex])
    }
  })

  const onDocumentPointerDown = event => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : []

    if (!path.includes(picker) && !picker.contains(event.target)) {
      setOpen(false)
    }
  }

  const onViewportChange = () => {
    if (isOpen) {
      positionMenu()
    }
  }

  doc.addEventListener('mousedown', onDocumentPointerDown)
  win?.addEventListener('scroll', onViewportChange, true)
  win?.addEventListener('resize', onViewportChange)

  return {
    el: picker,
    setForms(nextForms) {
      forms = Array.isArray(nextForms) ? nextForms : []
      filtered = [...forms]
      highlightedIndex = -1

      if (!isOpen) {
        syncInputFromSelection()
      }

      renderMenu()
    },
    setSelected(formId) {
      currentSelectedId = formId || ''

      if (!isOpen) {
        syncInputFromSelection()
      }
    },
    setLoading(loading) {
      input.disabled = Boolean(loading)
      picker.classList.toggle('is-loading', Boolean(loading))
      input.placeholder = loading
        ? i18n.t('Loading forms...')
        : i18n.t('Select a form')
    },
    destroy() {
      doc.removeEventListener('mousedown', onDocumentPointerDown)
      win?.removeEventListener('scroll', onViewportChange, true)
      win?.removeEventListener('resize', onViewportChange)
    },
  }
}

const placeholderPickers = new WeakMap()

function removeExistingPlaceholders(el) {
  el.querySelectorAll('.novti-pb-form-placeholder-host').forEach(host => {
    placeholderPickers.get(host)?.destroy()
    placeholderPickers.delete(host)
    host.remove()
  })
  el.querySelectorAll('#form-options').forEach(node => node.remove())
}

/**
 * Registers the searchable form picker as a GrapesJS trait for Component Settings.
 *
 * @param {*} editor
 */
export function registerFormSelectTrait(editor) {
  editor.TraitManager.addType('novti-form-select', {
    templateInput() {
      return ''
    },
    createInput({ component }) {
      const doc = document
      const host = doc.createElement('div')
      host.className = 'novti-pb-form-picker-host'
      host.setAttribute('style', 'display:block;width:auto;max-width:100%;min-width:0;box-sizing:border-box;position:relative;z-index:1;overflow:hidden;')

      const shadow = host.attachShadow({ mode: 'open' })
      injectEmptyStateStyles(shadow)

      const selectedId = resolveInitialTraitFormId(component)
      const picker = createFormPicker({
        doc,
        variant: 'sidebar',
        selectedId,
        onSelect: formId => {
          component.addAttributes({ form: formId })
        },
        onOpenChange: open => {
          host.classList.toggle('is-open', open)
          host.style.zIndex = open ? String(PICKER_MENU_Z_INDEX) : '1'
        },
      })

      shadow.appendChild(picker.el)
      this.formPicker = picker
      loadPickerForms(picker, selectedId)

      return host
    },
    onUpdate({ component }) {
      this.formPicker?.setSelected(component.getAttributes().form)
    },
    onEvent() {},
    removed() {
      this.formPicker?.destroy()
      this.formPicker = undefined
    },
  })
}

/**
 * Shows a Novti-styled empty state when no form is selected.
 *
 * @param {HTMLElement} el The form component element
 * @param {*} model The GrapesJS component model
 */
export function showNoFormNotice(el, model) {
  if (!el) {
    return
  }

  const doc = el.ownerDocument
  removeExistingPlaceholders(el)

  const host = doc.createElement('div')
  host.className = 'novti-pb-form-placeholder-host'
  host.setAttribute('style', 'display:block;width:100%;box-sizing:border-box;position:relative;z-index:5;overflow:visible;')

  const shadow = host.attachShadow({ mode: 'open' })
  injectEmptyStateStyles(shadow)

  const placeholder = doc.createElement('div')
  placeholder.className = 'novti-pb-form-placeholder'

  const icon = doc.createElement('div')
  icon.className = 'novti-pb-form-placeholder__icon'
  icon.innerHTML = formIconSvg()

  const title = doc.createElement('h3')
  title.className = 'novti-pb-form-placeholder__title'
  title.textContent = i18n.t('No form selected')

  const description = doc.createElement('p')
  description.className = 'novti-pb-form-placeholder__description'
  description.textContent = i18n.t('Choose a form to preview it on this page.')

  const picker = createFormPicker({
    doc,
    onSelect: formId => {
      model.setAttributes({ form: formId })
    },
    onOpenChange: open => {
      host.classList.toggle('is-open', open)
      host.style.zIndex = open ? String(PICKER_MENU_Z_INDEX) : '5'
    },
  })

  placeholder.appendChild(icon)
  placeholder.appendChild(title)
  placeholder.appendChild(description)
  placeholder.appendChild(picker.el)
  shadow.appendChild(placeholder)
  placeholderPickers.set(host, picker)
  el.appendChild(host)

  loadPickerForms(picker).finally(() => {
    if (!host.isConnected) {
      picker.destroy()
    }
  })
}
